import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import SubMenuEntry from '../SubMenuEntry'
import { useNavContext } from '../useNavContext'
import Button from '@toasttab/shared/src/components/atoms/Button/Button'
import { DesktopTabbedMenuProps } from '../ContentfulNavigation.d'

const DesktopTabbedMenu = ({
  dropdownContent,
  menuTitle
}: DesktopTabbedMenuProps) => {
  const [activeTab, setActiveTab] = useState(0)

  const { selectedParentMenu } = useNavContext()

  useEffect(() => {
    // Reset to the first tab if we change the active menu or close out of the menus
    if (selectedParentMenu !== menuTitle) {
      setActiveTab(0)
    }
  }, [menuTitle, selectedParentMenu, setActiveTab])

  const tabLinks: string[] = []
  const tabContent: any[] = []
  dropdownContent?.tabbedContent?.forEach((tab: any) => {
    tabLinks.push(tab.title)
    tabContent.push({ ...tab })
  })

  const wrapperClasses = classNames(
    'col-span-3 grid grid-cols-3 gap-x-6 gap-y-8 w-full rounded-br-lg overflow-y-scroll !no-scrollbar',
    {
      hidden: selectedParentMenu !== menuTitle
    }
  )

  return (
    <div className={wrapperClasses} data-testid='DesktopTabbedMenu'>
      <div className='border-r border-light flex flex-col py-10 pr-6'>
        {tabLinks?.map((link: string, i: number) => {
          const linkClasses = classNames(
            'flex text-[15px] p-2 w-full rounded-md bg-white hover:bg-neutral hover:text-default hover:after:content-icon-chevron-right after:ml-auto after:scale-75 after:h-4',
            {
              'bg-neutral after:content-icon-chevron-right': i === activeTab,
              'text-secondary': i !== activeTab
            }
          )
          return (
            <button
              className={linkClasses}
              data-testid='DesktopTabbedMenu-tabLink'
              key={`tabLink-${link.replace(/ /, '-').toLowerCase()}`}
              onClick={() => setActiveTab(i)}
            >
              {link}
            </button>
          )
        })}
        {dropdownContent?.sectionLink ? (
          <a
            className='block mt-auto text-default font-normal flex items-center'
            href={dropdownContent.sectionLink.url}
          >
            <span className='before:content-icon-all-products before:block before:leading-none pt-0.5 bg-neutral rounded-full flex items-center justify-center w-8 h-8 mr-1.5' />
            {dropdownContent.sectionLink.title}
          </a>
        ) : null}
      </div>
      <div className='col-span-2 grid place-content-stretch'>
        {tabContent?.map((content: any, i: number) => {
          const tabClasses = classNames(
            // Using row-span-full and col-span-full to overlap the content without using absolute positioning
            // This allows the wrapper to maintain the correct height, based on the longest content in the menu
            'row-span-full col-span-full py-10 transition-[opacity] duration-300',
            {
              'invisible opacity-0': i !== activeTab,
              'visible opacity-100': i === activeTab
            }
          )
          return (
            <div
              className={tabClasses}
              data-testid='DesktopTabbedMenu-content'
              key={`tabMenuContent-${content.heading
                ?.replace(/ /, '-')
                .toLowerCase()}`}
            >
              <div className='flex items-end mb-6'>
                <div className='mr-auto'>
                  <h3 className='heading-3'>{content.heading}</h3>

                  {content.description ? (
                    <p className='text-secondary mt-1'>{content.description}</p>
                  ) : null}
                </div>
                {content.ctaLink && content.ctaLabel ? (
                  <Button
                    classes='ml-4'
                    href={content.ctaLink}
                    size='medium'
                    variation='secondary'
                  >
                    {content.ctaLabel}
                  </Button>
                ) : null}
              </div>

              <SubMenuEntry
                hideEntryTitle
                key={`desktop-sub-menu-section-${content.heading}}`}
                sectionListClasses='grid grid-cols-2 gap-2 border-t border-light pt-4'
                {...content}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default DesktopTabbedMenu
